/**
 * ProfilePhotoUpload
 * Reusable photo upload card for Mentor & Mentee profile pages.
 *
 * Props:
 *   user        – the current auth user object
 *   updateUser  – updateUser() from useAuth()
 *   accentColor – optional hex colour for the border accent (default '#1DB954')
 */

import React, { useState, useRef } from 'react';

const MAX_MB    = 2;
const MAX_BYTES = MAX_MB * 1024 * 1024;

export default function ProfilePhotoUpload({ user, updateUser, accentColor = '#1DB954' }) {
  const [error,   setError]   = useState('');
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [dragging,setDragging]= useState(false);
  const inputRef = useRef(null);

  const processFile = (file) => {
    if (!file) return;
    setError('');
    setSaved(false);

    // ── File-type guard ────────────────────────────────────────
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Unsupported file type. Please upload a JPG, PNG or WebP image.');
      return;
    }

    // ── Size guard ────────────────────────────────────────────
    if (file.size > MAX_BYTES) {
      setError(
        `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). ` +
        `Maximum allowed size is ${MAX_MB} MB.`
      );
      return;
    }

    setSaving(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        // Center-crop to 300 × 300, compress to JPEG 85 %
        const canvas = document.createElement('canvas');
        const S = 300;
        canvas.width  = S;
        canvas.height = S;
        const ctx = canvas.getContext('2d');
        const min = Math.min(img.width, img.height);
        ctx.drawImage(
          img,
          (img.width  - min) / 2,
          (img.height - min) / 2,
          min, min,
          0, 0, S, S
        );
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        // TODO §BACKEND: PATCH /api/users/me { photoUrl }
        updateUser({ photoUrl: dataUrl });
        setSaving(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e) => {
    processFile(e.target.files?.[0]);
    e.target.value = ''; // allow re-selecting same file
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  const handleRemove = () => {
    // TODO §BACKEND: PATCH /api/users/me { photoUrl: '' }
    updateUser({ photoUrl: '' });
    setError('');
    setSaved(false);
  };

  const hasPPhoto = !!user?.photoUrl;

  return (
    <div className="db-card" style={{ marginBottom: 0 }}>
      {/* Section header */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', fontWeight: 800, fontSize: '.88rem', color: '#111827' }}>
        📸 Profile Photo
      </div>

      <div style={{ padding: '20px', display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* ── Left: current avatar / photo ────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <div style={{
            width: 96, height: 96, borderRadius: '50%',
            background: `#${user?.avatarBg || '1DB954'}`,
            overflow: 'hidden', position: 'relative',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 30, fontWeight: 800, color: '#fff',
            border: hasPPhoto ? `3px solid ${accentColor}` : '3px dashed #d1d5db',
            boxShadow: hasPPhoto ? `0 0 0 4px ${accentColor}22` : 'none',
            transition: 'border .2s, box-shadow .2s',
          }}>
            {hasPPhoto ? (
              <img
                src={user.photoUrl}
                alt={user?.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              user?.initials || user?.name?.charAt(0) || 'U'
            )}

            {/* Saving spinner overlay */}
            {saving && (
              <div style={{
                position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, borderRadius: '50%',
              }}>
                ⏳
              </div>
            )}
          </div>

          {/* Status label */}
          <div style={{ fontSize: '.72rem', fontWeight: 600, color: hasPPhoto ? accentColor : '#9ca3af', textAlign: 'center' }}>
            {hasPPhoto ? '✅ Photo set' : 'Using default avatar'}
          </div>
        </div>

        {/* ── Right: upload zone + controls ──────────────────── */}
        <div style={{ flex: 1, minWidth: 220 }}>

          {/* Drag-and-drop zone */}
          <label
            htmlFor={`ppu-${user?.id || 'photo'}`}
            onDragEnter={() => setDragging(true)}
            onDragLeave={() => setDragging(false)}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDrop={handleDrop}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '20px 16px', marginBottom: 10,
              border: `2px dashed ${dragging ? accentColor : '#e5e7eb'}`,
              borderRadius: 12, cursor: 'pointer', textAlign: 'center',
              background: dragging ? `${accentColor}0d` : '#fafafa',
              transition: 'border-color .15s, background .15s',
            }}
            onMouseEnter={e => { if (!dragging) { e.currentTarget.style.borderColor = accentColor; e.currentTarget.style.background = `${accentColor}0d`; } }}
            onMouseLeave={e => { if (!dragging) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fafafa'; } }}
          >
            <span style={{ fontSize: 28, marginBottom: 6, lineHeight: 1 }}>
              {saved ? '✅' : dragging ? '📂' : '📤'}
            </span>
            <span style={{ fontWeight: 700, fontSize: '.84rem', color: '#374151', marginBottom: 4 }}>
              {saved ? 'Photo updated!' : dragging ? 'Drop to upload' : 'Click to upload or drag & drop'}
            </span>
            <span style={{ fontSize: '.74rem', color: '#9ca3af' }}>
              JPG, PNG or WebP &nbsp;·&nbsp; Max {MAX_MB} MB
            </span>
          </label>

          <input
            id={`ppu-${user?.id || 'photo'}`}
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={handleInputChange}
          />

          {/* Error banner */}
          {error && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '9px 12px', marginBottom: 10,
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 8, fontSize: '.78rem', color: '#dc2626', lineHeight: 1.5,
            }}>
              <span style={{ flexShrink: 0 }}>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Action row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => inputRef.current?.click()}
              disabled={saving}
              style={{
                padding: '7px 14px', borderRadius: 8,
                border: `1.5px solid ${accentColor}`,
                background: '#fff', color: accentColor,
                fontWeight: 700, fontSize: '.78rem', cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? .5 : 1, transition: 'all .15s',
              }}
            >
              {hasPPhoto ? '🔄 Change Photo' : '📷 Upload Photo'}
            </button>

            {hasPPhoto && (
              <button
                onClick={handleRemove}
                disabled={saving}
                style={{
                  padding: '7px 14px', borderRadius: 8,
                  border: '1.5px solid #fecaca',
                  background: '#fff', color: '#ef4444',
                  fontWeight: 700, fontSize: '.78rem', cursor: 'pointer',
                  transition: 'all .15s',
                }}
              >
                🗑 Remove
              </button>
            )}
          </div>

          {/* Helper text */}
          <p style={{ margin: '10px 0 0', fontSize: '.72rem', color: '#9ca3af', lineHeight: 1.5 }}>
            Your photo will be shown on your profile and in mentor listings.
            Images are cropped to a square and compressed automatically.
          </p>
        </div>
      </div>
    </div>
  );
}
